import {
  Alert,
  Button,
  Group,
  Menu,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCode, IconEye } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';

type Vsebina = Shema['VsebinaIzpisaPodatki'];

/** Predloge dokumentov (hera-model zakonodaja.Dokument). */
const PREDLOGE = [
  { value: 'kupoprodajna', label: 'Kupoprodajna pogodba' },
  { value: 'posredniska', label: 'Posredniška pogodba' },
  { value: 'prevzemni', label: 'Prevzemni zapisnik' },
  { value: 'prevzemnica', label: 'Prevzemnica' },
  { value: 'garancija', label: 'Garancija' },
  { value: 'preizkusneTable', label: 'Preizkusne table' },
];

/**
 * Predloge dokumentov: besedilo z oznakami {{…}} (zamenjajo se s podatki),
 * velikosti pisave, logotip, predogled z vzorčnimi podatki (PDF s strežnika) in shranjevanje.
 */
export function Predloge() {
  const [tip, setTip] = useState('kupoprodajna');
  const predloga = useQuery({
    queryKey: ['predloga', tip],
    queryFn: ({ signal }) => podatki(api.GET('/api/vsebine-izpisov/{tip}', { signal, params: { path: { tip } } })),
  });
  return (
    <Stack gap="md">
      <Select
        label="Predloga"
        data={PREDLOGE}
        value={tip}
        onChange={(v) => v && setTip(v)}
        allowDeselect={false}
        maw={360}
      />
      {predloga.data ? (
        <UrejanjePredloge key={`${tip}-${predloga.dataUpdatedAt}`} tip={tip} shranjena={predloga.data} />
      ) : (
        <StanjeNalaganja
          napaka={predloga.error}
          ponovi={() => void predloga.refetch()}
          naslov="Predloge ni bilo mogoče naložiti"
        />
      )}
    </Stack>
  );
}

function UrejanjePredloge({ tip, shranjena }: { tip: string; shranjena: Vsebina }) {
  const odjemalec = useQueryClient();
  const [v, setV] = useState<Vsebina>(shranjena);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState<string | null>(null);
  const besedilo = useRef<HTMLTextAreaElement>(null);
  const oznake = useQuery({
    queryKey: ['oznake-predlog'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vsebine-izpisov/oznake', { signal })),
    staleTime: Infinity,
  });
  const spremenjeno = JSON.stringify(v) !== JSON.stringify(shranjena);
  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const opozori = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', opozori);
    return () => window.removeEventListener('beforeunload', opozori);
  }, [spremenjeno]);

  /** Vstavi oznako na mesto kazalca v besedilu. */
  const vstavi = (oznaka: string) => {
    const el = besedilo.current;
    const b = v.besedilo ?? '';
    const od = el?.selectionStart ?? b.length;
    const doK = el?.selectionEnd ?? b.length;
    setV({ ...v, besedilo: b.slice(0, od) + oznaka + b.slice(doK) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(od + oznaka.length, od + oznaka.length);
    });
  };

  const predogled = async () => {
    setNapaka(null);
    setDela('predogled');
    try {
      await odpriPdf(
        api.POST('/api/vsebine-izpisov/{tip}/predogled/pdf', { params: { path: { tip } }, body: v, parseAs: 'blob' }),
      );
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const shrani = async () => {
    setNapaka(null);
    setDela('shrani');
    try {
      const nova = await podatki(api.PUT('/api/vsebine-izpisov/{tip}', { params: { path: { tip } }, body: v }));
      odjemalec.setQueryData(['predloga', tip], nova);
      notifications.show({ color: 'green', message: 'Predloga je shranjena.' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  return (
    <Kartica
      naslov={PREDLOGE.find((p) => p.value === tip)?.label}
      dejanja={
        <Menu position="bottom-end" width={280}>
          <Menu.Target>
            <Button size="xs" variant="light" leftSection={<IconCode size={14} />}>
              Vstavi oznako
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <ScrollArea.Autosize mah={320}>
              {(oznake.data ?? []).map((o) => (
                <Menu.Item key={o} onClick={() => vstavi(o)} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {o}
                </Menu.Item>
              ))}
            </ScrollArea.Autosize>
          </Menu.Dropdown>
        </Menu>
      }
    >
      <Text size="xs" c="dimmed" mb="xs">
        Oznake {'{{…}}'} se ob izpisu zamenjajo s podatki; {'{{b}}'} krepko, {'{{h}}'} naslov, {'{{pb}}'} nova stran.
      </Text>
      <Textarea
        ref={besedilo}
        aria-label="Besedilo predloge"
        value={v.besedilo ?? ''}
        onChange={(e) => setV({ ...v, besedilo: e.currentTarget.value })}
        autosize
        minRows={12}
        maxRows={30}
        styles={{ input: { fontFamily: 'monospace', fontSize: 13 } }}
      />
      <Group mt="md" gap="md" align="flex-end" wrap="wrap">
        <NumberInput
          label="Velikost besedila"
          value={v.besediloVelikost ?? ''}
          onChange={(x) => setV({ ...v, besediloVelikost: typeof x === 'number' ? x : undefined })}
          min={4}
          max={30}
          allowDecimal={false}
          w={150}
        />
        <NumberInput
          label="Velikost naslova"
          value={v.naslovVelikost ?? ''}
          onChange={(x) => setV({ ...v, naslovVelikost: typeof x === 'number' ? x : undefined })}
          min={4}
          max={40}
          allowDecimal={false}
          w={150}
        />
        <Switch
          label="Prikaži logotip"
          checked={!!v.prikaziLogo}
          onChange={(e) => setV({ ...v, prikaziLogo: e.currentTarget.checked })}
          mb={10}
        />
      </Group>
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" mt="md" gap="xs">
        <Button
          variant="default"
          leftSection={<IconEye size={18} />}
          onClick={() => void predogled()}
          loading={dela === 'predogled'}
        >
          Predogled
        </Button>
        <Button onClick={() => void shrani()} loading={dela === 'shrani'} disabled={!spremenjeno}>
          Shrani
        </Button>
      </Group>
    </Kartica>
  );
}
